import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { NotificationFormProps } from './types';
import ToggleSlider from '../../../../../../common/components/xepp/toggle-slider/ToggleSlider';
import ConfirmationModal from '../../../../../../common/components/xepp/confirmation-modal/ConfirmationModal';
import styles from './NotificationForm.module.scss';
import { Input } from '../../../../../customer/components';

interface PushNotificationFormValues {
    title: string;
    message: string;
    sendViaEmail: boolean;
}

const PushNotificationForm = ({ onSubmit }: NotificationFormProps) => {
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingData, setPendingData] = useState<PushNotificationFormValues | null>(null);

    const { control, handleSubmit } = useForm<PushNotificationFormValues>({
        defaultValues: {
            title: '',
            message: '',
            sendViaEmail: false,
        },
    });

    const onFormSubmit = (data: PushNotificationFormValues) => {
        setPendingData(data);
        setIsConfirmOpen(true);
    };

    const handleConfirm = () => {
        if (!pendingData) return;
        setIsConfirmOpen(false);
        onSubmit({ notificationType: 'push', ...pendingData });
        setPendingData(null);
    };

    return (
        <>
        <form id="notification-form" className={styles.form} onSubmit={handleSubmit(onFormSubmit)}>
            <Controller
                name="title"
                control={control}
                rules={{ required: 'Notification title is required.', maxLength: { value: 100, message: 'Title must not exceed 100 characters.' } }}
                render={({ field, fieldState }) => (
                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <label className={styles.label}>Title</label>
                            <span className={styles.charCount}>{field.value.length} / 100</span>
                        </div>
                        <Input
                            variant="light"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder=""
                            error={fieldState.error?.message}
                        />
                    </div>
                )}
            />
            <Controller
                name="message"
                control={control}
                rules={{ required: 'Message body is required.', maxLength: { value: 200, message: 'Message must not exceed 200 characters.' } }}
                render={({ field, fieldState }) => (
                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <label className={styles.label}>Message</label>
                            <span className={styles.charCount}>{field.value.length} / 200</span>
                        </div>
                        <textarea
                            className={`${styles.textarea} ${fieldState.error ? styles.textareaError : ''}`}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder=""
                            rows={4}
                        />
                        {fieldState.error && <span className={styles.errorText}>{fieldState.error.message}</span>}
                    </div>
                )}
            />
            <Controller
                name="sendViaEmail"
                control={control}
                render={({ field }) => (
                    <div className={styles.toggleRow}>
                        <div className={styles.toggleRowMain}>
                            <span className={styles.toggleLabel}>Send via Email</span>
                            <ToggleSlider isActive={field.value} onChange={field.onChange} />
                        </div>
                    </div>
                )}
            />
        </form>
        <ConfirmationModal
            isOpen={isConfirmOpen}
            onClose={() => setIsConfirmOpen(false)}
            onConfirm={handleConfirm}
            title="Send Push Notification"
            message="Send push notification to all customers?"
            confirmText="Confirm"
            cancelText="Cancel"
            variant="info"
        />
        </>
    );
};

export default PushNotificationForm;
